"use strict";
/**
 * ЕДИНЫЙ РЕЕСТР ТИПОВ ProjectItem.
 *
 * Это единственное место, где описано поведение каждого из 15 типов элемента.
 * Из него выводятся:
 *   - схемы валидации (packages/validation);
 *   - формы быстрого и полного создания (apps/web);
 *   - колонки таблиц и профильные представления;
 *   - допустимые статусы и начальный статус при quick-add;
 *   - правила установки completed_at;
 *   - подписи интерфейса на русском языке.
 *
 * Добавление нового типа элемента = одна запись в ITEM_TYPE_DEFINITIONS.
 * Ни backend, ни frontend не должны содержать switch по типу элемента там,
 * где ответ можно получить из реестра.
 *
 * Принятые решения, отличающиеся от исходной спецификации:
 *   1. `project_items.status` — единственный источник истины о состоянии.
 *      Дублирующие properties (reviewStatus, verificationStatus) удалены.
 *   2. Статус ARCHIVED убран из всех типов: архивирование выражается
 *      колонкой `archived_at`, а не значением статуса.
 *   3. Текущая версия актива хранится только в asset_versions.is_current,
 *      properties.currentVersion удалён.
 *   4. Данные EXPENSE вынесены в типизированную таблицу expense_details.
 *   5. Статусы TOOL, ACCOUNT и EXPENSE в исходных документах определены не были
 *      и заданы здесь.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ALL_ITEM_STATUSES = exports.ITEM_TYPE_LABELS_RU = exports.KNOWLEDGE_ITEM_TYPES = exports.REVIEWABLE_ITEM_TYPES = exports.NEXT_ACTION_ITEM_TYPES = exports.ITEM_TYPE_DEFINITIONS = exports.EXPENSE_PAYMENT_STATUSES = exports.EXPENSE_CATEGORIES = exports.EXPENSE_RECURRENCES = exports.PROPERTY_KINDS = exports.ITEM_GROUP_LABELS_RU = exports.ITEM_GROUPS = exports.ITEM_TYPES = void 0;
exports.isItemType = isItemType;
exports.getItemTypeDefinition = getItemTypeDefinition;
exports.getItemTypesByGroup = getItemTypesByGroup;
exports.getStatusesForType = getStatusesForType;
exports.getStatusDefinition = getStatusDefinition;
exports.isValidStatusForType = isValidStatusForType;
exports.getInitialStatus = getInitialStatus;
exports.isTerminalStatus = isTerminalStatus;
exports.isOpenStatus = isOpenStatus;
exports.getPropertyDefinition = getPropertyDefinition;
exports.getJsonProperties = getJsonProperties;
exports.getDetailProperties = getDetailProperties;
exports.getQuickAddProperties = getQuickAddProperties;
// --- Типы элементов ------------------------------------------------------
/** Порядок соответствует сетке выбора типа в форме быстрого добавления. */
exports.ITEM_TYPES = [
    'NOTE',
    'SOURCE',
    'RESEARCH',
    'FACT',
    'METRIC',
    'HYPOTHESIS',
    'QUESTION',
    'DECISION',
    'TASK',
    'RISK',
    'BLOCKER',
    'ASSET',
    'TOOL',
    'ACCOUNT',
    'EXPENSE',
];
/** Смысловые группы (документ «Итоговое описание», §6). */
exports.ITEM_GROUPS = ['KNOWLEDGE', 'WORK', 'RESULT', 'RESOURCE', 'FINANCE'];
exports.ITEM_GROUP_LABELS_RU = {
    KNOWLEDGE: 'Знания',
    WORK: 'Работа и состояние',
    RESULT: 'Результаты',
    RESOURCE: 'Ресурсы',
    FINANCE: 'Финансы',
};
// --- Описание свойства ---------------------------------------------------
/**
 * Тип значения свойства. Определяет и правило валидации, и элемент управления
 * в форме, и способ отображения в таблице.
 */
exports.PROPERTY_KINDS = [
    'string',
    'text',
    'url',
    'email',
    'integer',
    'decimal',
    'money',
    'currency',
    'date',
    'datetime',
    'boolean',
    'enum',
    'userRefList',
];
// --- Часто переиспользуемые наборы опций ---------------------------------
const SOURCE_TYPE_OPTIONS = [
    { value: 'WEBSITE', labelRu: 'Сайт' },
    { value: 'ARTICLE', labelRu: 'Статья' },
    { value: 'REPORT', labelRu: 'Отчёт' },
    { value: 'DATASET', labelRu: 'Набор данных' },
    { value: 'DOCUMENT', labelRu: 'Документ' },
    { value: 'COMPETITOR', labelRu: 'Конкурент' },
    { value: 'EXAMPLE', labelRu: 'Пример' },
    { value: 'VIDEO', labelRu: 'Видео' },
    { value: 'OTHER', labelRu: 'Другое' },
];
const ASSET_TYPE_OPTIONS = [
    { value: 'DOCUMENT', labelRu: 'Документ' },
    { value: 'DESIGN', labelRu: 'Дизайн' },
    { value: 'CODE', labelRu: 'Код' },
    { value: 'SOURCE_ARCHIVE', labelRu: 'Архив исходников' },
    { value: 'BUILD', labelRu: 'Сборка' },
    { value: 'SCRIPT', labelRu: 'Скрипт' },
    { value: 'IMAGE', labelRu: 'Изображение' },
    { value: 'VIDEO', labelRu: 'Видео' },
    { value: 'PRESENTATION', labelRu: 'Презентация' },
    { value: 'RESEARCH_REPORT', labelRu: 'Отчёт об исследовании' },
    { value: 'DATASET', labelRu: 'Набор данных' },
    { value: 'TEMPLATE', labelRu: 'Шаблон' },
    { value: 'LINK', labelRu: 'Ссылка' },
    { value: 'OTHER', labelRu: 'Другое' },
];
const LEVEL_OPTIONS = [
    { value: 'LOW', labelRu: 'Низкая' },
    { value: 'MEDIUM', labelRu: 'Средняя' },
    { value: 'HIGH', labelRu: 'Высокая' },
    { value: 'CRITICAL', labelRu: 'Критическая' },
];
const BILLING_CYCLE_OPTIONS = [
    { value: 'FREE', labelRu: 'Бесплатно' },
    { value: 'MONTHLY', labelRu: 'Ежемесячно' },
    { value: 'QUARTERLY', labelRu: 'Ежеквартально' },
    { value: 'YEARLY', labelRu: 'Ежегодно' },
    { value: 'ONE_TIME', labelRu: 'Разово' },
    { value: 'USAGE_BASED', labelRu: 'По потреблению' },
];
exports.EXPENSE_RECURRENCES = ['ONE_TIME', 'MONTHLY', 'QUARTERLY', 'YEARLY', 'OTHER'];
const RECURRENCE_OPTIONS = [
    { value: 'ONE_TIME', labelRu: 'Разово' },
    { value: 'MONTHLY', labelRu: 'Ежемесячно' },
    { value: 'QUARTERLY', labelRu: 'Ежеквартально' },
    { value: 'YEARLY', labelRu: 'Ежегодно' },
    { value: 'OTHER', labelRu: 'Другое' },
];
exports.EXPENSE_CATEGORIES = [
    'INFRASTRUCTURE',
    'SOFTWARE',
    'MARKETING',
    'LEGAL',
    'CONTRACTOR',
    'RESEARCH',
    'OFFICE',
    'OTHER',
];
const EXPENSE_CATEGORY_OPTIONS = [
    { value: 'INFRASTRUCTURE', labelRu: 'Инфраструктура' },
    { value: 'SOFTWARE', labelRu: 'Программное обеспечение' },
    { value: 'MARKETING', labelRu: 'Маркетинг' },
    { value: 'LEGAL', labelRu: 'Юридические услуги' },
    { value: 'CONTRACTOR', labelRu: 'Подрядчики' },
    { value: 'RESEARCH', labelRu: 'Исследования' },
    { value: 'OFFICE', labelRu: 'Операционные расходы' },
    { value: 'OTHER', labelRu: 'Другое' },
];
exports.EXPENSE_PAYMENT_STATUSES = ['PLANNED', 'PAID', 'OVERDUE', 'CANCELLED'];
const PAYMENT_STATUS_OPTIONS = [
    { value: 'PLANNED', labelRu: 'Запланирован' },
    { value: 'PAID', labelRu: 'Оплачен' },
    { value: 'OVERDUE', labelRu: 'Просрочен' },
    { value: 'CANCELLED', labelRu: 'Отменён' },
];
// --- Реестр --------------------------------------------------------------
exports.ITEM_TYPE_DEFINITIONS = [
    {
        type: 'NOTE',
        labelRu: 'Заметка',
        pluralRu: 'Заметки',
        descriptionRu: 'Свободная проектная заметка без специальной структуры.',
        group: 'KNOWLEDGE',
        icon: 'StickyNote',
        statuses: [{ value: 'ACTIVE', labelRu: 'Активна', tone: 'neutral', isOpen: true }],
        initialStatus: 'ACTIVE',
        supportsNextAction: false,
        supportsReview: false,
        supportsAttachments: true,
        supportsVersions: false,
        supportsPriority: false,
        supportsDueDate: false,
        quickAddProperties: [],
        properties: [],
        emptyStateRu: 'Заметок пока нет. Запишите наблюдение, которое иначе потеряется в переписке.',
    },
    {
        type: 'SOURCE',
        labelRu: 'Источник',
        pluralRu: 'Источники',
        descriptionRu: 'Внешний материал, на который опираются выводы проекта.',
        group: 'KNOWLEDGE',
        icon: 'Link2',
        statuses: [
            { value: 'ACTIVE', labelRu: 'Актуален', tone: 'success', isOpen: true },
            { value: 'NEEDS_REVIEW', labelRu: 'Требует проверки', tone: 'warning', isOpen: true },
            { value: 'OUTDATED', labelRu: 'Устарел', tone: 'danger' },
        ],
        initialStatus: 'ACTIVE',
        supportsNextAction: false,
        supportsReview: false,
        supportsAttachments: true,
        supportsVersions: false,
        supportsPriority: false,
        supportsDueDate: false,
        quickAddProperties: ['url'],
        properties: [
            {
                key: 'url',
                kind: 'url',
                labelRu: 'Ссылка',
                required: true,
                maxLength: 2000,
                hintRu: 'Адрес исходного материала.',
            },
            {
                key: 'sourceType',
                kind: 'enum',
                labelRu: 'Тип источника',
                required: false,
                options: SOURCE_TYPE_OPTIONS,
            },
            { key: 'publisher', kind: 'string', labelRu: 'Издатель', required: false, maxLength: 300 },
            { key: 'publicationDate', kind: 'date', labelRu: 'Дата публикации', required: false },
            {
                key: 'accessedAt',
                kind: 'date',
                labelRu: 'Дата обращения',
                required: false,
                hintRu: 'Когда материал был прочитан. Важно для источников, которые меняются.',
            },
            { key: 'geography', kind: 'string', labelRu: 'География', required: false, maxLength: 200 },
        ],
        emptyStateRu: 'Источников пока нет. Добавьте ссылку на материал, на который опирается проект.',
    },
    {
        type: 'RESEARCH',
        labelRu: 'Исследование',
        pluralRu: 'Исследования',
        descriptionRu: 'Исследовательская работа: вопрос, метод и полученный вывод.',
        group: 'KNOWLEDGE',
        icon: 'Microscope',
        statuses: [
            { value: 'TODO', labelRu: 'Не начато', tone: 'neutral', isOpen: true },
            { value: 'IN_PROGRESS', labelRu: 'В работе', tone: 'progress', isOpen: true },
            { value: 'READY_FOR_REVIEW', labelRu: 'На проверке', tone: 'warning', isOpen: true },
            { value: 'APPROVED', labelRu: 'Принято', tone: 'success', isTerminal: true },
            { value: 'NEEDS_CHANGES', labelRu: 'Нужны правки', tone: 'danger', isOpen: true },
            { value: 'CANCELLED', labelRu: 'Отменено', tone: 'neutral', isTerminal: true },
        ],
        initialStatus: 'TODO',
        supportsNextAction: true,
        supportsReview: true,
        supportsAttachments: true,
        supportsVersions: false,
        supportsPriority: true,
        supportsDueDate: true,
        quickAddProperties: ['researchQuestion'],
        properties: [
            {
                key: 'researchQuestion',
                kind: 'text',
                labelRu: 'Исследовательский вопрос',
                required: true,
                maxLength: 1000,
                hintRu: 'На какой именно вопрос отвечает эта работа.',
            },
            { key: 'method', kind: 'text', labelRu: 'Метод', required: false, maxLength: 2000 },
            { key: 'conclusion', kind: 'text', labelRu: 'Вывод', required: false, maxLength: 4000 },
            {
                key: 'limitations',
                kind: 'text',
                labelRu: 'Ограничения',
                required: false,
                maxLength: 2000,
                hintRu: 'Чего этот результат не доказывает.',
            },
        ],
        emptyStateRu: 'Исследований пока нет. Добавьте вопрос, метод и результат первой исследовательской работы.',
    },
    {
        type: 'FACT',
        labelRu: 'Факт',
        pluralRu: 'Факты',
        descriptionRu: 'Утверждение, которое команда считает подтверждённым.',
        group: 'KNOWLEDGE',
        icon: 'BadgeCheck',
        statuses: [
            { value: 'UNVERIFIED', labelRu: 'Не проверен', tone: 'warning', isOpen: true },
            { value: 'VERIFIED', labelRu: 'Подтверждён', tone: 'success', isTerminal: true },
            { value: 'DISPUTED', labelRu: 'Оспаривается', tone: 'danger', isOpen: true },
            { value: 'OUTDATED', labelRu: 'Устарел', tone: 'neutral' },
        ],
        initialStatus: 'UNVERIFIED',
        supportsNextAction: false,
        supportsReview: false,
        supportsAttachments: true,
        supportsVersions: false,
        supportsPriority: false,
        supportsDueDate: false,
        quickAddProperties: ['statement'],
        properties: [
            {
                key: 'statement',
                kind: 'text',
                labelRu: 'Формулировка',
                required: true,
                maxLength: 2000,
                hintRu: 'Одно проверяемое утверждение.',
            },
            {
                key: 'basisType',
                kind: 'enum',
                labelRu: 'Основание',
                required: false,
                options: [
                    { value: 'SOURCE', labelRu: 'Внешний источник' },
                    { value: 'INTERNAL_OBSERVATION', labelRu: 'Внутреннее наблюдение команды' },
                ],
                hintRu: 'Факт должен опираться на источник либо быть явно отмечен как наблюдение команды.',
            },
        ],
        emptyStateRu: 'Фактов пока нет. Зафиксируйте утверждение и свяжите его с источником.',
    },
    {
        type: 'METRIC',
        labelRu: 'Метрика',
        pluralRu: 'Метрики',
        descriptionRu: 'Числовое наблюдение за конкретный период.',
        group: 'KNOWLEDGE',
        icon: 'ChartNoAxesColumn',
        statuses: [
            { value: 'ACTIVE', labelRu: 'Актуальна', tone: 'success', isOpen: true },
            { value: 'OUTDATED', labelRu: 'Устарела', tone: 'neutral' },
        ],
        initialStatus: 'ACTIVE',
        supportsNextAction: false,
        supportsReview: false,
        supportsAttachments: true,
        supportsVersions: false,
        supportsPriority: false,
        supportsDueDate: false,
        quickAddProperties: ['value', 'unit'],
        properties: [
            {
                key: 'metricName',
                kind: 'string',
                labelRu: 'Название показателя',
                required: false,
                maxLength: 300,
            },
            { key: 'value', kind: 'decimal', labelRu: 'Значение', required: true },
            { key: 'unit', kind: 'string', labelRu: 'Единица измерения', required: true, maxLength: 80 },
            { key: 'periodStart', kind: 'date', labelRu: 'Начало периода', required: false },
            { key: 'periodEnd', kind: 'date', labelRu: 'Конец периода', required: false },
            { key: 'geography', kind: 'string', labelRu: 'География', required: false, maxLength: 200 },
            { key: 'segment', kind: 'string', labelRu: 'Сегмент', required: false, maxLength: 300 },
            {
                key: 'methodology',
                kind: 'text',
                labelRu: 'Методика',
                required: false,
                maxLength: 2000,
                hintRu: 'Как получено значение. Новое значение за новый период — отдельная метрика.',
            },
        ],
        emptyStateRu: 'Метрик пока нет. Добавьте число, единицу и период наблюдения.',
    },
    {
        type: 'HYPOTHESIS',
        labelRu: 'Гипотеза',
        pluralRu: 'Гипотезы',
        descriptionRu: 'Предположение, которое ещё предстоит проверить.',
        group: 'KNOWLEDGE',
        icon: 'FlaskConical',
        statuses: [
            { value: 'UNTESTED', labelRu: 'Не проверена', tone: 'neutral', isOpen: true },
            { value: 'TESTING', labelRu: 'Проверяется', tone: 'progress', isOpen: true },
            { value: 'VALIDATED', labelRu: 'Подтверждена', tone: 'success', isTerminal: true },
            { value: 'INVALIDATED', labelRu: 'Опровергнута', tone: 'danger', isTerminal: true },
        ],
        initialStatus: 'UNTESTED',
        supportsNextAction: false,
        supportsReview: false,
        supportsAttachments: true,
        supportsVersions: false,
        supportsPriority: true,
        supportsDueDate: false,
        quickAddProperties: ['statement'],
        properties: [
            { key: 'statement', kind: 'text', labelRu: 'Формулировка', required: true, maxLength: 2000 },
            {
                key: 'validationMethod',
                kind: 'text',
                labelRu: 'Способ проверки',
                required: false,
                maxLength: 2000,
                hintRu: 'Что именно подтвердит или опровергнет гипотезу.',
            },
            {
                key: 'validationResult',
                kind: 'text',
                labelRu: 'Результат проверки',
                required: false,
                maxLength: 2000,
            },
        ],
        emptyStateRu: 'Гипотез пока нет. Сформулируйте предположение и способ его проверки.',
    },
    {
        type: 'QUESTION',
        labelRu: 'Вопрос',
        pluralRu: 'Вопросы',
        descriptionRu: 'Открытый вопрос, на который у проекта пока нет ответа.',
        group: 'WORK',
        icon: 'CircleHelp',
        statuses: [
            { value: 'OPEN', labelRu: 'Открыт', tone: 'warning', isOpen: true },
            { value: 'IN_REVIEW', labelRu: 'Прорабатывается', tone: 'progress', isOpen: true },
            { value: 'ANSWERED', labelRu: 'Отвечен', tone: 'success', isTerminal: true },
        ],
        initialStatus: 'OPEN',
        supportsNextAction: true,
        supportsReview: false,
        supportsAttachments: true,
        supportsVersions: false,
        supportsPriority: true,
        supportsDueDate: true,
        quickAddProperties: [],
        properties: [
            { key: 'answer', kind: 'text', labelRu: 'Ответ', required: false, maxLength: 4000 },
        ],
        emptyStateRu: 'Открытых вопросов нет. Зафиксируйте то, что пока непонятно проекту.',
    },
    {
        type: 'DECISION',
        labelRu: 'Решение',
        pluralRu: 'Решения',
        descriptionRu: 'Зафиксированное решение команды и его обоснование.',
        group: 'WORK',
        icon: 'Gavel',
        statuses: [
            { value: 'ACTIVE', labelRu: 'Действует', tone: 'success', isOpen: true },
            { value: 'SUPERSEDED', labelRu: 'Заменено', tone: 'neutral' },
            { value: 'REVOKED', labelRu: 'Отменено', tone: 'danger' },
        ],
        initialStatus: 'ACTIVE',
        supportsNextAction: false,
        supportsReview: false,
        supportsAttachments: true,
        supportsVersions: false,
        supportsPriority: false,
        supportsDueDate: false,
        quickAddProperties: [],
        properties: [
            { key: 'decision', kind: 'text', labelRu: 'Решение', required: true, maxLength: 2000 },
            {
                key: 'rationale',
                kind: 'text',
                labelRu: 'Обоснование',
                required: false,
                maxLength: 4000,
                hintRu: 'Почему выбран именно этот вариант и какие рассматривались ещё.',
            },
            { key: 'decidedBy', kind: 'userRefList', labelRu: 'Кто принял', required: false },
            { key: 'decidedAt', kind: 'datetime', labelRu: 'Когда принято', required: false },
        ],
        emptyStateRu: 'Решений пока нет. Если решение принято в переписке, зафиксируйте его здесь — иначе оно потеряется.',
    },
    {
        type: 'TASK',
        labelRu: 'Задача',
        pluralRu: 'Задачи',
        descriptionRu: 'Конкретное действие с ответственным и сроком.',
        group: 'WORK',
        icon: 'SquareCheck',
        statuses: [
            { value: 'TODO', labelRu: 'Не начата', tone: 'neutral', isOpen: true },
            { value: 'IN_PROGRESS', labelRu: 'В работе', tone: 'progress', isOpen: true },
            { value: 'BLOCKED', labelRu: 'Заблокирована', tone: 'danger', isOpen: true },
            { value: 'DONE', labelRu: 'Выполнена', tone: 'success', isTerminal: true },
            { value: 'CANCELLED', labelRu: 'Отменена', tone: 'neutral', isTerminal: true },
        ],
        initialStatus: 'TODO',
        supportsNextAction: true,
        supportsReview: false,
        supportsAttachments: true,
        supportsVersions: false,
        supportsPriority: true,
        supportsDueDate: true,
        quickAddProperties: [],
        properties: [
            {
                key: 'outcomeSummary',
                kind: 'text',
                labelRu: 'Итог',
                required: false,
                maxLength: 2000,
                hintRu: 'Краткое резюме результата при завершении задачи.',
            },
        ],
        emptyStateRu: 'Задач пока нет. Добавьте конкретное действие с ответственным.',
    },
    {
        type: 'RISK',
        labelRu: 'Риск',
        pluralRu: 'Риски',
        descriptionRu: 'Потенциальная проблема, которая ещё не наступила.',
        group: 'WORK',
        icon: 'TriangleAlert',
        statuses: [
            { value: 'OPEN', labelRu: 'Открыт', tone: 'warning', isOpen: true },
            { value: 'MONITORING', labelRu: 'Под наблюдением', tone: 'progress', isOpen: true },
            { value: 'MITIGATED', labelRu: 'Снижен', tone: 'success', isTerminal: true },
            { value: 'OCCURRED', labelRu: 'Наступил', tone: 'danger', isOpen: true },
            { value: 'CLOSED', labelRu: 'Закрыт', tone: 'neutral', isTerminal: true },
        ],
        initialStatus: 'OPEN',
        supportsNextAction: false,
        supportsReview: false,
        supportsAttachments: true,
        supportsVersions: false,
        supportsPriority: true,
        supportsDueDate: false,
        quickAddProperties: [],
        properties: [
            {
                key: 'probability',
                kind: 'enum',
                labelRu: 'Вероятность',
                required: false,
                options: LEVEL_OPTIONS,
            },
            { key: 'impact', kind: 'enum', labelRu: 'Влияние', required: false, options: LEVEL_OPTIONS },
            {
                key: 'mitigation',
                kind: 'text',
                labelRu: 'Меры снижения',
                required: false,
                maxLength: 2000,
            },
        ],
        emptyStateRu: 'Рисков пока нет. Опишите то, что может помешать проекту.',
    },
    {
        type: 'BLOCKER',
        labelRu: 'Блокер',
        pluralRu: 'Блокеры',
        descriptionRu: 'Уже существующая помеха, которая останавливает работу.',
        group: 'WORK',
        icon: 'OctagonX',
        statuses: [
            { value: 'OPEN', labelRu: 'Открыт', tone: 'danger', isOpen: true },
            { value: 'IN_PROGRESS', labelRu: 'Решается', tone: 'progress', isOpen: true },
            { value: 'RESOLVED', labelRu: 'Снят', tone: 'success', isTerminal: true },
            { value: 'IGNORED', labelRu: 'Принят как есть', tone: 'neutral', isTerminal: true },
        ],
        initialStatus: 'OPEN',
        supportsNextAction: true,
        supportsReview: false,
        supportsAttachments: true,
        supportsVersions: false,
        supportsPriority: true,
        supportsDueDate: false,
        quickAddProperties: [],
        properties: [
            {
                key: 'blockedSince',
                kind: 'date',
                labelRu: 'Блокирует с',
                required: false,
                hintRu: 'Возраст блокера показывается на Dashboard.',
            },
            { key: 'resolution', kind: 'text', labelRu: 'План снятия', required: false, maxLength: 2000 },
        ],
        emptyStateRu: 'Блокеров нет. Это хорошая новость.',
    },
    {
        type: 'ASSET',
        labelRu: 'Актив',
        pluralRu: 'Активы',
        descriptionRu: 'Созданный результат проекта: документ, дизайн, код, отчёт.',
        group: 'RESULT',
        icon: 'Package',
        statuses: [
            { value: 'DRAFT', labelRu: 'Черновик', tone: 'neutral', isOpen: true },
            { value: 'READY_FOR_REVIEW', labelRu: 'На проверке', tone: 'warning', isOpen: true },
            { value: 'APPROVED', labelRu: 'Принят', tone: 'success', isTerminal: true },
            { value: 'NEEDS_CHANGES', labelRu: 'Нужны правки', tone: 'danger', isOpen: true },
            { value: 'OUTDATED', labelRu: 'Устарел', tone: 'neutral' },
        ],
        initialStatus: 'DRAFT',
        supportsNextAction: false,
        supportsReview: true,
        supportsAttachments: true,
        supportsVersions: true,
        supportsPriority: false,
        supportsDueDate: true,
        quickAddProperties: ['assetType'],
        properties: [
            {
                key: 'assetType',
                kind: 'enum',
                labelRu: 'Тип актива',
                required: true,
                options: ASSET_TYPE_OPTIONS,
            },
            {
                key: 'externalUrl',
                kind: 'url',
                labelRu: 'Внешняя ссылка',
                required: false,
                maxLength: 2000,
                hintRu: 'Если результат хранится в GitHub, Figma или Google Docs.',
            },
        ],
        warningRu: 'Не прикладывайте сюда файлы с паролями, ключами и recovery-кодами. Секреты хранятся во внешнем менеджере паролей.',
        emptyStateRu: 'Активов пока нет. Добавьте созданный результат: документ, дизайн или отчёт.',
    },
    {
        type: 'TOOL',
        labelRu: 'Инструмент',
        pluralRu: 'Инструменты',
        descriptionRu: 'Сервис или инструмент, который использует проект.',
        group: 'RESOURCE',
        icon: 'Wrench',
        statuses: [
            { value: 'ACTIVE', labelRu: 'Используется', tone: 'success', isOpen: true },
            { value: 'TRIAL', labelRu: 'Пробный период', tone: 'progress', isOpen: true },
            { value: 'DEPRECATED', labelRu: 'Выводится из использования', tone: 'warning', isOpen: true },
            { value: 'CANCELLED', labelRu: 'Отключён', tone: 'neutral', isTerminal: true },
        ],
        initialStatus: 'ACTIVE',
        supportsNextAction: false,
        supportsReview: false,
        supportsAttachments: false,
        supportsVersions: false,
        supportsPriority: false,
        supportsDueDate: false,
        quickAddProperties: ['serviceUrl'],
        properties: [
            {
                key: 'serviceUrl',
                kind: 'url',
                labelRu: 'Адрес сервиса',
                required: false,
                maxLength: 2000,
            },
            { key: 'vendor', kind: 'string', labelRu: 'Поставщик', required: false, maxLength: 300 },
            { key: 'purpose', kind: 'text', labelRu: 'Назначение', required: false, maxLength: 1000 },
            {
                key: 'billingCycle',
                kind: 'enum',
                labelRu: 'Тарификация',
                required: false,
                options: BILLING_CYCLE_OPTIONS,
            },
        ],
        emptyStateRu: 'Инструментов пока нет. Добавьте сервисы, которыми пользуется проект.',
    },
    {
        type: 'ACCOUNT',
        labelRu: 'Аккаунт',
        pluralRu: 'Аккаунты',
        descriptionRu: 'Реестр проектных аккаунтов без хранения секретов.',
        group: 'RESOURCE',
        icon: 'KeyRound',
        statuses: [
            { value: 'ACTIVE', labelRu: 'Активен', tone: 'success', isOpen: true },
            { value: 'SUSPENDED', labelRu: 'Приостановлен', tone: 'warning', isOpen: true },
            { value: 'CLOSED', labelRu: 'Закрыт', tone: 'neutral', isTerminal: true },
        ],
        initialStatus: 'ACTIVE',
        supportsNextAction: false,
        supportsReview: false,
        supportsAttachments: false,
        supportsVersions: false,
        supportsPriority: false,
        supportsDueDate: false,
        quickAddProperties: ['serviceName', 'loginEmail'],
        properties: [
            { key: 'serviceName', kind: 'string', labelRu: 'Сервис', required: true, maxLength: 300 },
            {
                key: 'loginEmail',
                kind: 'email',
                labelRu: 'Логин / email',
                required: false,
                maxLength: 320,
            },
            {
                key: 'accountUrl',
                kind: 'url',
                labelRu: 'Адрес личного кабинета',
                required: false,
                maxLength: 2000,
            },
            {
                key: 'credentialReference',
                kind: 'string',
                labelRu: 'Где лежит доступ',
                required: false,
                maxLength: 300,
                hintRu: 'Только название записи во внешнем менеджере паролей, например «Vault / Project / GitHub».',
            },
            {
                key: 'lastAccessReviewAt',
                kind: 'date',
                labelRu: 'Последняя ревизия доступа',
                required: false,
            },
            {
                key: 'accessNotes',
                kind: 'text',
                labelRu: 'Примечания о доступе',
                required: false,
                maxLength: 2000,
            },
        ],
        warningRu: 'Не сохраняйте здесь пароль, API-ключ, приватный ключ или recovery-код. В поле «Где лежит доступ» указывается только ссылка на запись во внешнем защищённом хранилище.',
        emptyStateRu: 'Аккаунтов пока нет. Заведите реестр, чтобы доступы не терялись вместе с людьми.',
    },
    {
        type: 'EXPENSE',
        labelRu: 'Расход',
        pluralRu: 'Расходы',
        descriptionRu: 'Управленческий реестр расходов проекта.',
        group: 'FINANCE',
        icon: 'Receipt',
        statuses: [
            { value: 'ACTIVE', labelRu: 'Действует', tone: 'success', isOpen: true },
            { value: 'CANCELLED', labelRu: 'Отменён', tone: 'neutral', isTerminal: true },
        ],
        initialStatus: 'ACTIVE',
        supportsNextAction: false,
        supportsReview: false,
        supportsAttachments: true,
        supportsVersions: false,
        supportsPriority: false,
        supportsDueDate: false,
        detailTable: 'expense_details',
        quickAddProperties: ['amountMinor', 'currency'],
        properties: [
            {
                key: 'amountMinor',
                kind: 'money',
                labelRu: 'Сумма',
                required: true,
                min: 0,
                storage: 'detail',
                hintRu: 'Хранится в минорных единицах: 12.50 € — это 1250.',
            },
            { key: 'currency', kind: 'currency', labelRu: 'Валюта', required: true, storage: 'detail' },
            {
                key: 'category',
                kind: 'enum',
                labelRu: 'Категория',
                required: false,
                options: EXPENSE_CATEGORY_OPTIONS,
                storage: 'detail',
            },
            {
                key: 'vendor',
                kind: 'string',
                labelRu: 'Поставщик',
                required: false,
                maxLength: 300,
                storage: 'detail',
            },
            {
                key: 'expenseDate',
                kind: 'date',
                labelRu: 'Дата расхода',
                required: true,
                storage: 'detail',
            },
            {
                key: 'recurrence',
                kind: 'enum',
                labelRu: 'Периодичность',
                required: true,
                options: RECURRENCE_OPTIONS,
                storage: 'detail',
            },
            {
                key: 'nextPaymentDate',
                kind: 'date',
                labelRu: 'Следующий платёж',
                required: false,
                storage: 'detail',
            },
            {
                key: 'paymentStatus',
                kind: 'enum',
                labelRu: 'Статус оплаты',
                required: false,
                options: PAYMENT_STATUS_OPTIONS,
                storage: 'detail',
            },
        ],
        emptyStateRu: 'Расходов пока нет. Добавьте подписки и разовые платежи проекта.',
    },
];
// --- Производные структуры и helpers -------------------------------------
const REGISTRY_BY_TYPE = new Map(exports.ITEM_TYPE_DEFINITIONS.map((definition) => [definition.type, definition]));
function isItemType(value) {
    return typeof value === 'string' && REGISTRY_BY_TYPE.has(value);
}
/** Возвращает описание типа. Бросает исключение для неизвестного типа. */
function getItemTypeDefinition(type) {
    const definition = REGISTRY_BY_TYPE.get(type);
    if (!definition) {
        throw new Error(`Неизвестный тип элемента: ${String(type)}`);
    }
    return definition;
}
function getItemTypesByGroup(group) {
    return exports.ITEM_TYPE_DEFINITIONS.filter((definition) => definition.group === group);
}
function getStatusesForType(type) {
    return getItemTypeDefinition(type).statuses;
}
function getStatusDefinition(type, status) {
    return getItemTypeDefinition(type).statuses.find((candidate) => candidate.value === status);
}
function isValidStatusForType(type, status) {
    return getStatusDefinition(type, status) !== undefined;
}
function getInitialStatus(type) {
    return getItemTypeDefinition(type).initialStatus;
}
/** Является ли статус конечным — по нему проставляется completed_at. */
function isTerminalStatus(type, status) {
    return getStatusDefinition(type, status)?.isTerminal === true;
}
/** Считается ли элемент открытым — для Dashboard и подсчётов по направлениям. */
function isOpenStatus(type, status) {
    return getStatusDefinition(type, status)?.isOpen === true;
}
function getPropertyDefinition(type, key) {
    return getItemTypeDefinition(type).properties.find((property) => property.key === key);
}
/** Свойства, хранящиеся в jsonb `properties` (без колонок detail-таблиц). */
function getJsonProperties(type) {
    return getItemTypeDefinition(type).properties.filter((property) => (property.storage ?? 'properties') === 'properties');
}
/** Свойства, хранящиеся в типизированной detail-таблице. */
function getDetailProperties(type) {
    return getItemTypeDefinition(type).properties.filter((property) => property.storage === 'detail');
}
/** Поля, показываемые прямо в компактной форме быстрого добавления. */
function getQuickAddProperties(type) {
    const definition = getItemTypeDefinition(type);
    return definition.quickAddProperties
        .map((key) => definition.properties.find((property) => property.key === key))
        .filter((property) => property !== undefined);
}
/** Типы, для которых допустим флаг «следующее действие». */
exports.NEXT_ACTION_ITEM_TYPES = exports.ITEM_TYPE_DEFINITIONS.filter((definition) => definition.supportsNextAction).map((definition) => definition.type);
/** Типы, участвующие в процессе проверки материалов. */
exports.REVIEWABLE_ITEM_TYPES = exports.ITEM_TYPE_DEFINITIONS.filter((definition) => definition.supportsReview).map((definition) => definition.type);
/** Типы, считающиеся накопленным знанием (блок «Новые знания» на Dashboard). */
exports.KNOWLEDGE_ITEM_TYPES = getItemTypesByGroup('KNOWLEDGE').map((definition) => definition.type);
exports.ITEM_TYPE_LABELS_RU = Object.fromEntries(exports.ITEM_TYPE_DEFINITIONS.map((definition) => [definition.type, definition.labelRu]));
/** Плоский список всех статусов всех типов — для валидации фильтров списка. */
exports.ALL_ITEM_STATUSES = [
    ...new Set(exports.ITEM_TYPE_DEFINITIONS.flatMap((definition) => definition.statuses.map((status) => status.value))),
];
//# sourceMappingURL=item-registry.js.map